// @flow
import React, { Fragment, useContext } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import { COLORS } from 'Components/utils/color/colors';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { SeeMore } from 'Components/frameworks/see-more/SeeMore.jsx';

type Props = {
  className?: string,
  isExclusiveCollection: boolean,
  items: Array<{
    color?: Colors,
    iconName: IconName,
    label: string,
    trackingData?: TrackingData,
    url?: string,
  }>,
  seeLessLabel?: string,
  seeMoreLabel?: string,
};

export function ProductStrengths(props: Props) {
  const { className, isExclusiveCollection, items = [], seeLessLabel, seeMoreLabel } = props;

  const { isTablet } = useContext(ResponsiveContext);

  if (isEmpty(items)) return null;

  const content = (
    <div className={classnames(className, 'flex flex-wrap -mx-2')}>
      {items.map(({ color = COLORS.BLUE, iconName, label, trackingData, url }, idx) => (
        <Fragment key={label}>
          {!!idx && (
            <span className="fadeInLong hidden sm:block font-serif mt-3 opacity-50 text-gray-light text-7xl">
              /
            </span>
          )}
          <TrackClick
            as={url ? DynamicLink : 'div'}
            className={classnames(
              `w-1/2 sm:w-auto sm:flex-1 max-w-1/2 flex flex-col items-center p-2 text-${color} text-xs sm:text-base text-center`,
              {
                [`focus:text-${color}-active hover:text-${color}-active transition-color`]: url,
              },
            )}
            trackingData={trackingData}
            url={url}
          >
            <Icon
              className="mb-2 sm:mb-3"
              classNameInner="scaleIn"
              name={iconName}
              width="3.75rem"
            />
            <span className={classnames('block w-full', { 'font-serif': isExclusiveCollection })}>
              {label}
            </span>
          </TrackClick>
        </Fragment>
      ))}
    </div>
  );

  return isTablet ? (
    content
  ) : (
    <SeeMore
      classNameButton="text-center"
      isCollapseEnabled
      seeLessLabel={seeLessLabel}
      seeMoreLabel={seeMoreLabel}
      seeMoreSize={150}
    >
      {content}
    </SeeMore>
  );
}
